import { formatDate } from '@/utils/function'
import { AssetFile } from '../../../types'
import { FileText, Download, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/ui/data-table'
import { useAssetFileAccess } from '../../../hooks/useAssetFileAccess'

interface FilesTabProps {
    files: AssetFile[] | null
    /**
     * Resolved by the API for the current requester. Undefined means an older payload shape,
     * which is treated as "allowed" so the tab keeps working during a partial deploy.
     */
    canDownload?: boolean
}

const FilesTab = ({ files, canDownload }: FilesTabProps) => {
    const { t } = useTranslation('asset')
    const { openAssetFile, downloadAssetFile } = useAssetFileAccess()

    const allowDownload = canDownload !== false

    // Handle null files case
    const filesList = files || []

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const handlePreview = (file: AssetFile) => {
        openAssetFile(file, allowDownload)
    }

    const handleDownload = (e: React.MouseEvent, file: AssetFile) => {
        e.stopPropagation()
        downloadAssetFile(file)
    }

    const columns: ColumnDef<AssetFile>[] = [
        {
            id: t('files.fileName'),
            accessorKey: 'file_name',
            header: t('files.fileName'),
            cell: ({ row }) => {
                const file = row.original
                return (
                    <div
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => handlePreview(file)}
                    >
                        <FileText className="h-4 w-4 text-blue-500" />
                        <span className="font-medium hover:underline text-blue-600">
                            {file.file_name || 'Unknown'}
                        </span>
                    </div>
                )
            },
        },
        {
            id: t('files.size'),
            accessorKey: 'file_size',
            header: t('files.size'),
            cell: ({ row }) => {
                return row.original.file_size
                    ? formatFileSize(row.original.file_size)
                    : '-'
            },
        },
        {
            id: t('files.uploadedAt'),
            accessorKey: 'created_at',
            header: t('files.uploadedAt'),
            cell: ({ row }) => formatDate(row.original.created_at),
        },
        {
            id: t('common.action'),
            header: () => (
                <div className="text-center">{t('common.action')}</div>
            ),
            cell: ({ row }) => {
                const file = row.original

                // Not rendered at all rather than merely disabled: a greyed-out control invites
                // people to re-enable it in devtools, and the click would still fire.
                if (!allowDownload) {
                    return (
                        <div
                            className="flex items-center justify-center gap-1 text-muted-foreground"
                            title={t('files.downloadRestrictedHint')}
                        >
                            <Lock className="h-4 w-4" />
                            <span className="text-xs">
                                {t('files.viewOnly')}
                            </span>
                        </div>
                    )
                }

                return (
                    <div className="text-center">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDownload(e, file)}
                        >
                            <Download className="h-4 w-4" />
                        </Button>
                    </div>
                )
            },
        },
    ]

    return (
        <div className="bg-white rounded-md p-4">
            <DataTable columns={columns} data={filesList} />
        </div>
    )
}

export default FilesTab
